import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheckIcon, DollarSignIcon, UsersIcon } from 'lucide-react';
import { TrackerState, User, LicenseData, MichiganLicenseData, US_STATES, DEFAULT_OFFICERS, createDefaultLicense, createDefaultMichiganLicense, getUserLicensedStateCount } from './types';
import { UserLicenseModal } from './UserLicenseModal';
import { StateLicenseModal } from './StateLicenseModal';
import { StateModal } from './StateModal';
import { MichiganModal } from './MichiganModal';
import { LicenseMatrix } from './LicenseMatrix';
const STORAGE_KEY = 'nmls-license-tracker-v3';
function getInitialState(): TrackerState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as TrackerState;
      if (Array.isArray(parsed.users) && typeof parsed.licenses === 'object') {
        return {
          users: parsed.users || [],
          licenses: parsed.licenses || {},
          michiganLicenses: parsed.michiganLicenses || {}
        };
      }
    }
  } catch {
    // Fall through
  }
  return {
    users: DEFAULT_OFFICERS,
    licenses: {},
    michiganLicenses: {}
  };
}
export function LicenseTracker() {
  const [state, setState] = useState<TrackerState>(getInitialState);
  // Modal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedStateName, setSelectedStateName] = useState<string | null>(null);
  // Detail modal: specific user + state
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [detailStateName, setDetailStateName] = useState<string | null>(null);
  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);
  // --- License Updates ---
  const handleUpdateLicense = useCallback((userId: string, stateName: string, license: LicenseData) => {
    setState(prev => ({
      ...prev,
      licenses: {
        ...prev.licenses,
        [userId]: {
          ...(prev.licenses[userId] || {}),
          [stateName]: license
        }
      }
    }));
  }, []);
  const handleUpdateMichiganLicense = useCallback((userId: string, license: MichiganLicenseData) => {
    setState(prev => ({
      ...prev,
      michiganLicenses: {
        ...prev.michiganLicenses,
        [userId]: license
      }
    }));
  }, []);
  const handleAddUserToState = useCallback((userId: string, stateName: string) => {
    if (stateName === 'Michigan') {
      setState(prev => {
        if (prev.michiganLicenses[userId]) return prev;
        return {
          ...prev,
          michiganLicenses: {
            ...prev.michiganLicenses,
            [userId]: createDefaultMichiganLicense()
          }
        };
      });
    } else {
      setState(prev => {
        if (prev.licenses[userId]?.[stateName]) return prev;
        return {
          ...prev,
          licenses: {
            ...prev.licenses,
            [userId]: {
              ...(prev.licenses[userId] || {}),
              [stateName]: createDefaultLicense()
            }
          }
        };
      });
    }
  }, []);
  // --- Open detail for user+state ---
  const handleOpenUserStateDetail = useCallback((userId: string, stateName: string) => {
    setDetailUserId(userId);
    setDetailStateName(stateName);
  }, []);
  // --- Calculations ---
  const stats = useMemo(() => {
    let totalLicensed = 0;
    let totalInProgress = 0;
    let totalCost = 0;
    for (const user of state.users) {
      const counts = getUserLicensedStateCount(user.id, state.licenses, state.michiganLicenses);
      totalLicensed += counts.licensed;
      totalInProgress += counts.inProgress;
      // Sum costs from regular licenses
      const userLicenses = state.licenses[user.id] || {};
      for (const lic of Object.values(userLicenses)) {
        // Use charges if available, fall back to legacy cost field
        const chargesTotal = (lic.charges || []).reduce((a, c) => a + c.amount, 0);
        totalCost += chargesTotal > 0 ? chargesTotal : lic.cost || 0;
        totalCost += lic.renewals.reduce((a, r) => a + (r.fee || 0), 0);
      }
      // Sum costs from Michigan
      const mi = state.michiganLicenses[user.id];
      if (mi) {
        totalCost += mi.stepCosts.reduce((a, c) => a + (c || 0), 0);
        totalCost += mi.renewals.reduce((a, r) => a + (r.fee || 0), 0);
      }
    }
    return {
      totalLicensed,
      totalInProgress,
      totalCost,
      totalUsers: state.users.length
    };
  }, [state]);
  // --- Find user/license for detail modal ---
  const detailUser = detailUserId ? state.users.find(u => u.id === detailUserId) || null : null;
  const selectedUser = selectedUserId ? state.users.find(u => u.id === selectedUserId) || null : null;
  return <div className="h-screen w-full bg-[#0a0f1e] text-slate-200 flex flex-col overflow-hidden" data-id="element-52">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent flex-shrink-0" data-id="element-53" />

      <div className="flex flex-col flex-1 min-h-0 px-4 py-3" data-id="element-54">
        {/* Header */}
        <header className="flex-shrink-0 mb-3" data-id="element-55">
          <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-56">
            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-2" data-id="element-57">
              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-58">
                <UsersIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-59" />
                <span className="text-xs text-slate-400" data-id="element-60">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-61">
                    {stats.totalUsers}
                  </span>{' '}
                  officers
                </span>
              </div>

              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-62">
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]" data-id="element-63" />
                <span className="text-xs text-slate-400" data-id="element-64">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-65">
                    {stats.totalLicensed}
                  </span>{' '}
                  licensed
                </span>
              </div>

              {stats.totalInProgress > 0 && <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-66">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.8)]" data-id="element-67" />
                  <span className="text-xs text-slate-400" data-id="element-68">
                    <span className="text-white font-bold text-sm tabular-nums" data-id="element-69">
                      {stats.totalInProgress}
                    </span>{' '}
                    in progress
                  </span>
                </div>}

              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-70">
                <DollarSignIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-71" />
                <span className="text-xs text-slate-400" data-id="element-72">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-73">
                    ${stats.totalCost.toLocaleString()}
                  </span>{' '}
                  invested
                </span>
              </div>
            </div>
          </div>
        </header>

        <LicenseMatrix users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} onCellClick={handleOpenUserStateDetail} onUserClick={setSelectedUserId} data-id="element-74" />
      </div>

      {/* --- Modals --- */}

      {/* User License Overview (click user → see all their states) */}
      {selectedUser && <UserLicenseModal user={selectedUser} isOpen={true} onClose={() => setSelectedUserId(null)} licenses={state.licenses[selectedUser.id] || {}} michiganLicense={state.michiganLicenses[selectedUser.id]} onOpenState={stateName => {
      handleOpenUserStateDetail(selectedUser.id, stateName);
    }} data-id="element-75" />}

      {/* State License Overview (click state → see all users in that state) */}
      {selectedStateName && !selectedUserId && <StateLicenseModal stateName={selectedStateName} isOpen={true} onClose={() => setSelectedStateName(null)} users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} onUpdateLicense={handleUpdateLicense} onUpdateMichiganLicense={handleUpdateMichiganLicense} onAddUserToState={handleAddUserToState} onOpenUserStateDetail={handleOpenUserStateDetail} data-id="element-76" />}

      {/* Detail: specific user + specific state */}
      {detailUser && detailStateName && detailStateName !== 'Michigan' && <StateModal user={detailUser} stateName={detailStateName} isOpen={true} onClose={() => {
      setDetailUserId(null);
      setDetailStateName(null);
    }} license={state.licenses[detailUser.id]?.[detailStateName] || createDefaultLicense()} onUpdateLicense={license => {
      handleUpdateLicense(detailUser.id, detailStateName!, license);
    }} data-id="element-77" />}

      {/* Detail: specific user + Michigan */}
      {detailUser && detailStateName === 'Michigan' && <MichiganModal user={detailUser} isOpen={true} onClose={() => {
      setDetailUserId(null);
      setDetailStateName(null);
    }} license={state.michiganLicenses[detailUser.id] || createDefaultMichiganLicense()} onUpdateLicense={license => {
      handleUpdateMichiganLicense(detailUser.id, license);
    }} data-id="element-78" />}
    </div>;
}